import { useState, useEffect, useMemo } from 'react';
import { Lock, ShieldCheck, Clock, AlertTriangle, Loader2, FileText } from 'lucide-react';
import {
  parseShareCodeFromLocation,
  decodeShareData,
  isEncryptedShareCode,
  verifyPin,
  isExpired,
  renderShareDocHtml,
  pinFromPhone,
  type ShareDocPayload,
} from '@/lib/shareLink';

/**
 * 客户专属只读单据查看器（独立路由 #/share?d=xxx）
 * - 不进入主应用，无任何编辑入口
 * - 凭客户手机号后4位验证查看
 * - 支持过期时间
 */
export function ShareViewer() {
  const [rawCode] = useState<string | null>(() => parseShareCodeFromLocation());
  const [payload, setPayload] = useState<ShareDocPayload | null>(null);
  const [phase, setPhase] = useState<'loading' | 'pin' | 'view' | 'error'>(() => {
    if (!rawCode) return 'error';
    return isEncryptedShareCode(rawCode) ? 'pin' : 'loading';
  });
  const [errorMsg, setErrorMsg] = useState(() => rawCode ? '' : '分享链接格式不正确，请联系商家重新发送。');
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [checking, setChecking] = useState(false);
  const [left, setLeft] = useState(5);

  useEffect(() => {
    if (!rawCode) return;
    // v2 加密链接：无法凭空解密（必须口令），直接进入口令验证阶段
    if (isEncryptedShareCode(rawCode)) return;
    // 旧版未加密链接（可立即解析）
    decodeShareData(rawCode).then((data) => {
      if (!data) {
        setPhase('error');
        setErrorMsg('分享链接已损坏或无法解析，请联系商家重新发送。');
        return;
      }
      if (isExpired(data)) {
        setPhase('error');
        setErrorMsg('该分享链接已过期，请联系商家重新发送。');
        return;
      }
      setPayload(data);
      setPhase(data.pinHash ? 'pin' : 'view');
    });
  }, [rawCode]);

  const docHtml = useMemo(() => (payload ? renderShareDocHtml(payload) : ''), [payload]);

  const handleVerify = async () => {
    if (!rawCode) return;
    if (!/^\d{4}$/.test(pin.trim())) {
      setPinError('请输入4位数字（收货人手机号后4位）');
      return;
    }
    setChecking(true);
    await new Promise((r) => setTimeout(r, 400)); // 防暴力枚举限速
    const data = await decodeShareData(rawCode, pin.trim());
    if (!data) {
      setLeft((n) => n - 1);
      if (left <= 1) {
        setPhase('error');
        setErrorMsg('验证失败次数过多，链接已锁定。请联系商家重新发送。');
      } else {
        setPinError(`口令不正确，还可尝试 ${left - 1} 次`);
      }
      setChecking(false);
      return;
    }
    if (isExpired(data)) {
      setPhase('error');
      setErrorMsg('该分享链接已过期，请联系商家重新发送。');
      setChecking(false);
      return;
    }
    // 解密成功后校验 PIN hash（防御 PIN 离线枚举后的篡改）
    const expectedPin = pinFromPhone(data.customerPhone);
    if (!verifyPin(data, pin.trim()) && !(expectedPin && verifyPin(data, expectedPin))) {
      setLeft((n) => n - 1);
      if (left <= 1) {
        setPhase('error');
        setErrorMsg('验证失败次数过多，链接已锁定。请联系商家重新发送。');
      } else {
        setPinError(`口令不正确，还可尝试 ${left - 1} 次`);
      }
      setChecking(false);
      return;
    }
    setPayload(data);
    setPhase('view');
    setChecking(false);
  };

  if (phase === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-3 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-[13px]">正在打开分享的单据…</span>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 p-8 text-center shadow-sm">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-[16px] font-semibold text-zinc-900 mb-2">无法查看</h1>
          <p className="text-[13px] text-zinc-500 leading-relaxed">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (phase === 'pin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-zinc-50 p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-emerald-600" />
          </div>
          <h1 className="text-[16px] font-semibold text-zinc-900 text-center mb-1">
            {payload?.docTitle || '单据'} · 身份验证
          </h1>
          <p className="text-[12.5px] text-zinc-500 text-center leading-relaxed mb-6">
            本单据仅限收货人 <span className="font-medium text-zinc-700">{payload?.customerName || '指定客户'}</span> 查看<br />
            请输入您的手机号后4位
          </p>
          <input
            value={pin}
            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
            onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
            inputMode="numeric"
            autoFocus
            placeholder="• • • •"
            className="w-full h-12 text-center text-[22px] tracking-[0.6em] font-semibold border border-zinc-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 mb-2"
          />
          {pinError && <p className="text-[12px] text-red-500 text-center mb-2">{pinError}</p>}
          <button
            onClick={handleVerify}
            disabled={checking || pin.length < 4}
            className="w-full h-11 bg-emerald-600 text-white rounded-xl font-medium text-[14px] hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            查看单据
          </button>
          <p className="text-[11px] text-zinc-400 text-center mt-4 leading-relaxed">
            此链接为专属链接，转发给他人无法查看
          </p>
        </div>
      </div>
    );
  }

  // 只读单据视图
  return (
    <div className="min-h-screen bg-zinc-100">
      {/* 顶部提示条 */}
      <div className="sticky top-0 z-10 bg-emerald-600 text-white">
        <div className="max-w-3xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[12.5px] min-w-0">
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span className="truncate">只读单据 · 仅供 {payload?.customerName} 查看，无编辑权限</span>
          </div>
          {payload?.expireAt ? (
            <span className="flex items-center gap-1 text-[11px] opacity-80 shrink-0">
              <Clock className="w-3 h-3" />
              {new Date(payload.expireAt).toLocaleDateString('zh-CN')} 前有效
            </span>
          ) : null}
        </div>
      </div>
      {/* 单据内容：白底居中，手机端可横向滚动 */}
      <div className="py-4 px-2 flex justify-center">
        <div className="bg-white shadow-sm rounded-lg overflow-x-auto max-w-3xl w-full">
          <iframe
            title="单据内容"
            srcDoc={docHtml}
            sandbox="allow-same-origin"
            className="w-full min-h-[600px] border-0"
            style={{ height: 'auto', minHeight: 600 }}
            onLoad={(e) => {
              // 自适应 iframe 高度
              const f = e.currentTarget;
              try {
                const h = f.contentDocument?.body?.scrollHeight;
                if (h) f.style.height = `${h + 20}px`;
              } catch { /* ignore */ }
            }}
          />
        </div>
      </div>
      <div className="pb-8 text-center">
        <p className="text-[11px] text-zinc-400 flex items-center justify-center gap-1.5">
          <FileText className="w-3 h-3" />
          昌发香行 · 专属只读单据，如需修改请联系商家
        </p>
      </div>
    </div>
  );
}
